import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../api/client";
import TimeSelect from "../../components/TimeSelect";
import { applyStoredOrder, compareProjects, fmtSidebarDate, NO_BU_KEY, NO_BU_LABEL, priorityTier, readPinnedIds, readStoredOrder, writePinnedIds, writeStoredOrder, } from "./projectsUtils";
export function SidebarProjectList({ projects, units, activeId, onSelect, canManage, currentUserId, expanded, }) {
    const [tab, setTab] = useState(() => {
        try {
            const v = localStorage.getItem("pm-tab");
            if (v === "all" || v === "mine" || v === "due7" || v === "meetings")
                return v;
        }
        catch {
            /* fall through */
        }
        return "all";
    });
    const [query, setQuery] = useState("");
    const [pinnedIds, setPinnedIds] = useState(readPinnedIds);
    function changeTab(t) {
        setTab(t);
        try {
            localStorage.setItem("pm-tab", t);
        }
        catch {
            /* ignore */
        }
    }
    function togglePin(id) {
        setPinnedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id))
                next.delete(id);
            else
                next.add(id);
            writePinnedIds(next);
            return next;
        });
    }
    // First-pass filter: chip + search.
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        // "Due ≤ 7d" window: everything due on or before 7 days out — which
        // includes anything ALREADY overdue (no lower bound). Compared as
        // YYYY-MM-DD strings — project.due_date is a date-only string.
        const horizon = new Date();
        horizon.setDate(horizon.getDate() + 7);
        const horizonIso = horizon.toISOString().slice(0, 10);
        return projects.filter((p) => {
            if (tab === "mine") {
                if (!currentUserId)
                    return false;
                if (!(p.assignee_ids ?? []).includes(currentUserId))
                    return false;
            }
            else if (tab === "meetings") {
                if (p.type !== "recurring_event" && p.type !== "one_time_event")
                    return false;
            }
            else if (tab === "due7") {
                // Overdue projects must surface here too — only the upper bound
                // (horizon) gates the list; past-due dates always qualify.
                if (!p.due_date)
                    return false;
                if (p.due_date > horizonIso)
                    return false;
            }
            if (q && !p.name.toLowerCase().includes(q))
                return false;
            return true;
        });
    }, [projects, tab, query, currentUserId]);
    // Pinned slice — pinned projects float to the top of the visible list
    // regardless of which tab is active (still respect search match).
    const pinnedRows = useMemo(() => filtered.filter((p) => pinnedIds.has(p.id)).sort(compareProjects), [filtered, pinnedIds]);
    const pinnedSet = useMemo(() => new Set(pinnedRows.map((p) => p.id)), [pinnedRows]);
    const rest = useMemo(() => filtered.filter((p) => !pinnedSet.has(p.id)), [filtered, pinnedSet]);
    // Pull completed projects into their own bucket BEFORE the other
    // section splits so a completed meeting or one-off doesn't double-
    // appear under both Complete and its type-specific section. The user
    // sees finished work at the bottom of the sidebar, separated.
    // The bottom "Complete" section is for finished standard projects +
    // recurring meetings only. One-off tasks and one-time events have their
    // OWN completed roll-ups inside their widgets (OneOffTasksWidget /
    // OneTimeEventsWidget), so excluding them here stops a finished one-off /
    // event from showing up in two places — the bug that produced three
    // "Completed" headers instead of two.
    const completed = rest
        .filter((p) => p.status === "completed" &&
        p.type !== "one_off_task" &&
        p.type !== "one_time_event")
        .sort(compareProjects);
    const activeRest = rest.filter((p) => p.status !== "completed");
    // Bucket the remaining (active) projects by type so the sidebar makes
    // it obvious which rows are full projects vs meetings vs one-off
    // tasks. One-off tasks get their own section regardless of BU or
    // role — they used to be lumped into the BU-grouped "standard"
    // sections when they had a business unit, which made projects and
    // one-off tasks hard to tell apart.
    const meetings = activeRest.filter((p) => p.type === "recurring_event");
    const oneTimeEvents = activeRest.filter((p) => p.type === "one_time_event");
    const oneOffTasks = activeRest
        .filter((p) => p.type === "one_off_task")
        .sort(compareProjects);
    const reservedIds = new Set([
        ...meetings.map((p) => p.id),
        ...oneTimeEvents.map((p) => p.id),
        ...oneOffTasks.map((p) => p.id),
    ]);
    const standard = activeRest.filter((p) => !reservedIds.has(p.id));
    const byUnit = new Map();
    for (const p of standard) {
        const key = p.business_unit_id ?? NO_BU_KEY;
        const arr = byUnit.get(key) ?? [];
        arr.push(p);
        byUnit.set(key, arr);
    }
    for (const arr of byUnit.values())
        arr.sort(compareProjects);
    meetings.sort(compareProjects);
    const unitNameById = new Map(units.map((u) => [u.id, u.name]));
    const orderedUnitKeys = Array.from(byUnit.keys()).sort((a, b) => {
        if (a === NO_BU_KEY)
            return 1;
        if (b === NO_BU_KEY)
            return -1;
        return (unitNameById.get(a) ?? "Unknown").localeCompare(unitNameById.get(b) ?? "Unknown");
    });
    const rowProps = {
        activeId,
        onSelect,
        pinnedIds,
        onTogglePin: togglePin,
        expanded,
        units,
    };
    return (_jsxs("div", { className: "pm-list", children: [_jsxs("div", { className: "pm-filters", role: "tablist", "aria-label": "Filter projects", children: [_jsx("button", { type: "button", role: "tab", "aria-selected": tab === "all", className: `pm-filter-pill ${tab === "all" ? "active" : ""}`, onClick: () => changeTab("all"), title: "Every project in any business unit you belong to", children: "My Biz Units" }), _jsx("button", { type: "button", role: "tab", "aria-selected": tab === "mine", className: `pm-filter-pill ${tab === "mine" ? "active" : ""}`, onClick: () => changeTab("mine"), children: "Mine" }), _jsx("button", { type: "button", role: "tab", "aria-selected": tab === "due7", className: `pm-filter-pill ${tab === "due7" ? "active" : ""}`, onClick: () => changeTab("due7"), title: "Projects that are overdue or due in the next 7 days", children: "Due \u2264 7d" }), _jsx("button", { type: "button", role: "tab", "aria-selected": tab === "meetings", className: `pm-filter-pill ${tab === "meetings" ? "active" : ""}`, onClick: () => changeTab("meetings"), children: "Meetings" })] }), _jsxs("div", { className: "pm-search", children: [_jsx("span", { className: "pm-search-icon", "aria-hidden": "true", children: "\u2315" }), _jsx("input", { type: "text", placeholder: "Find a project\u2026", value: query, onChange: (e) => setQuery(e.target.value) }), query && (_jsx("button", { type: "button", className: "pm-search-clear", onClick: () => setQuery(""), "aria-label": "Clear search", children: "\u00D7" }))] }), filtered.length === 0 && (_jsxs("div", { className: "muted small pm-empty", children: [query
                        ? `No projects match "${query}".`
                        : tab === "mine"
                            ? "Nothing assigned to you yet."
                            : tab === "meetings"
                                ? "No meetings."
                                : tab === "due7"
                                    ? "Nothing due in the next 7 days."
                                    : "No projects yet.", canManage && !query && " Click + New to create one."] })), _jsxs("div", { className: "pm-completed-widgets", children: [_jsx(CompletedThisMonthStrip, { projects: projects, activeId: activeId, onSelect: onSelect }), canManage && _jsx(OneOffSummaryWidget, {})] }), pinnedRows.length > 0 && (_jsxs("section", { className: "pm-section", children: [_jsxs("div", { className: "pm-section-label pm-section-pinned", children: ["\u2605 PINNED \u00B7 ", pinnedRows.length] }), _jsx(DraggableProjectList, { projects: pinnedRows, storageKey: "pm-order-pinned", ...rowProps })] })), orderedUnitKeys.map((key) => {
                const list = byUnit.get(key);
                if (list.length === 0)
                    return null;
                const label = key === NO_BU_KEY ? NO_BU_LABEL : unitNameById.get(key) ?? "Unknown";
                return (_jsxs("section", { className: "pm-section", children: [_jsxs("div", { className: "pm-section-label", children: [label.toUpperCase(), " \u00B7 ", list.length] }), _jsx(DraggableProjectList, { projects: list, storageKey: `pm-order-${key}`, ...rowProps })] }, key));
            }), meetings.length > 0 && (_jsxs("section", { className: "pm-section", children: [_jsxs("div", { className: "pm-section-label", children: ["\u21BB RECURRING \u00B7 ", meetings.length] }), _jsx(DraggableProjectList, { projects: meetings, storageKey: "pm-order-meetings", ...rowProps })] })), completed.length > 0 && (_jsx(CompleteSection, { projects: completed, rowProps: rowProps })), _jsx(OneOffTasksWidget, { activeId: activeId, onSelect: onSelect }), _jsx(OneTimeEventsWidget, { activeId: activeId, onSelect: onSelect, canManage: canManage })] }));
}
// Trophy strip at the top of the P&M list — celebrates projects
// completed in the current calendar month. Renders nothing if there's
// nothing to celebrate yet this month (no need to nag with an empty
// "0 wins" pill). Click → opens the project detail, where Reopen lives.
function CompletedThisMonthStrip({ projects, activeId, onSelect, }) {
    // "This calendar month" in the user's local timezone — completed_at
    // is UTC, but month boundaries align well enough that the small TZ
    // drift on the 1st/last day isn't worth a server round-trip.
    const monthStart = useMemo(() => {
        const d = new Date();
        return new Date(d.getFullYear(), d.getMonth(), 1).getTime();
    }, []);
    const monthLabel = useMemo(() => new Date().toLocaleString(undefined, {
        month: "long",
        year: "numeric",
    }), []);
    // Time spent per completed-this-month project (Deep Focus blocks + extra
    // logged), fetched once so each win shows how long it took.
    const [hoursByProject, setHoursByProject] = useState({});
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const d = await api.get("/projects/summary/completed-hours");
                if (!cancelled)
                    setHoursByProject(d.hours_by_project ?? {});
            }
            catch {
                /* non-fatal — rows just omit the hours */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);
    const wins = useMemo(() => {
        const out = [];
        for (const p of projects) {
            if (p.status !== "completed")
                continue;
            if (!p.completed_at)
                continue;
            // Recurring meetings, one-time events, and one-off tasks are
            // operational/administrative items — not deliverables. They
            // would drown out genuine project wins on the trophy strip,
            // so the celebration is reserved for type="standard" only
            // (matches the server-side Momentum emit gate).
            if (p.type !== "standard")
                continue;
            const t = Date.parse(p.completed_at);
            if (Number.isNaN(t) || t < monthStart)
                continue;
            out.push(p);
        }
        // Most-recent first — newest win at the top reads like a "latest
        // achievement" feed rather than a chronological log.
        out.sort((a, b) => (b.completed_at ?? "").localeCompare(a.completed_at ?? ""));
        return out;
    }, [projects, monthStart]);
    if (wins.length === 0)
        return null;
    return (_jsxs("section", { className: "pm-completed-month", "aria-label": "Completed this month", children: [_jsxs("header", { className: "pm-completed-month-head", children: [_jsxs("span", { className: "pm-completed-month-title", children: ["\uD83C\uDFC6 Completed this month \u00B7 ", monthLabel] }), _jsxs("span", { className: "pm-completed-month-count", children: [wins.length, " ", wins.length === 1 ? "win" : "wins"] })] }), _jsx("ul", { className: "pm-completed-month-list", children: wins.map((p) => {
                    const when = p.completed_at
                        ? new Date(p.completed_at).toLocaleDateString(undefined, {
                            month: "short",
                            day: "numeric",
                        })
                        : "";
                    const isActive = p.id === activeId;
                    return (_jsx("li", { children: _jsxs("button", { type: "button", className: `pm-completed-month-row ${isActive ? "active" : ""}`, onClick: () => onSelect(p.id), title: p.assignee_names && p.assignee_names.length > 0
                                ? `Open ${p.name}\nContributors: ${p.assignee_names.join(", ")}`
                                : `Open ${p.name}`, children: [_jsx("span", { className: "pm-completed-month-row-name", children: p.name }), p.assignee_names && p.assignee_names.length > 0 && (_jsxs("span", { className: "pm-completed-month-row-team muted small", children: ["by ", p.assignee_names.join(", ")] })), hoursByProject[p.id] != null && hoursByProject[p.id] > 0 && (_jsxs("span", { className: "pm-completed-month-row-hours muted small", children: [hoursByProject[p.id], "h"] })), _jsx("span", { className: "pm-completed-month-row-when", children: when })] }) }, p.id));
                }) })] }));
}
// Right-hand companion to the "Completed this month" strip: a roll-up of
// one-off tasks completed this month, grouped by business unit, with hours
// (Deep Focus blocks + extra logged time) and top contributors. Admin /
// manager only — it's a management summary. Self-fetches on mount + focus.
function OneOffSummaryWidget() {
    const [summary, setSummary] = useState(null);
    const [loaded, setLoaded] = useState(false);
    async function load() {
        try {
            const data = await api.get("/projects/summary/one-off");
            setSummary(data);
        }
        catch {
            setSummary(null);
        }
        finally {
            setLoaded(true);
        }
    }
    useEffect(() => {
        load();
        const onFocus = () => load();
        window.addEventListener("focus", onFocus);
        return () => window.removeEventListener("focus", onFocus);
    }, []);
    if (!loaded)
        return null;
    return (_jsxs("section", { className: "pm-oneoff-summary", "aria-label": "Completed one-off tasks summary", children: [_jsxs("header", { className: "pm-oneoff-summary-head", children: [_jsxs("span", { className: "pm-oneoff-summary-title", children: ["\u26A1 One-off tasks \u00B7 ", summary?.month_label ?? "this month"] }), summary && summary.total_tasks > 0 && (_jsxs("span", { className: "pm-oneoff-summary-count", children: [summary.total_tasks, " done \u00B7 ", summary.total_hours, "h"] }))] }), !summary || summary.total_tasks === 0 ? (_jsx("p", { className: "muted small", style: { margin: "0.4rem 0 0" }, children: "No one-off tasks completed this month yet." })) : (_jsx("ul", { className: "pm-oneoff-summary-list", children: summary.by_business_unit.map((bu) => (_jsxs("li", { className: "pm-oneoff-summary-bu", children: [_jsxs("div", { className: "pm-oneoff-summary-bu-head", children: [_jsx("span", { className: "pm-oneoff-summary-bu-name", children: bu.business_unit_name }), _jsxs("span", { className: "pm-oneoff-summary-bu-stats muted small", children: [bu.tasks_completed, " ", bu.tasks_completed === 1 ? "task" : "tasks", " \u00B7 ", bu.hours, "h"] })] }), bu.top_contributors.length > 0 && (_jsx("div", { className: "pm-oneoff-summary-contribs", children: bu.top_contributors.map((c) => (_jsxs("span", { className: "pill", title: `${c.user_name}: ${c.hours}h`, children: [c.user_name, " \u00B7 ", c.hours, "h"] }, c.user_name))) }))] }, bu.business_unit_id ?? bu.business_unit_name))) }))] }));
}
function CompleteSection({ projects, rowProps, }) {
    const [open, setOpen] = useState(() => {
        try {
            return localStorage.getItem("pm-complete-open") === "1";
        }
        catch {
            return false;
        }
    });
    function toggle() {
        const next = !open;
        setOpen(next);
        try {
            localStorage.setItem("pm-complete-open", next ? "1" : "0");
        }
        catch {
            /* ignore quota errors */
        }
    }
    return (_jsxs("section", { className: "pm-section pm-section-complete", children: [_jsxs("button", { type: "button", className: "pm-complete-head", onClick: toggle, "aria-expanded": open, children: [_jsxs("span", { children: ["\u2713 Completed projects \u00B7 ", projects.length] }), _jsx("span", { className: "pm-complete-caret", "aria-hidden": "true", children: open ? "▾" : "▸" })] }), open && (_jsx(DraggableProjectList, { projects: projects, storageKey: "pm-order-complete", ...rowProps }))] }));
}
// One-off tasks widget — a dedicated Projects & Meetings section fed by
// /me/one-off-tasks. One-off tasks are created straight from "+ New
// block" on the calendar and live outside the project/roadmap/kill-list
// flow, so they get their own widget here rather than being mixed into
// the BU-grouped project list. Self-fetching (mount + window focus) so a
// one-off task created on the calendar shows up without a manual reload.
export function OneOffTasksWidget({ activeId, onSelect, refreshSignal = 0, }) {
    const [rows, setRows] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [completedOpen, setCompletedOpen] = useState(() => {
        try {
            return localStorage.getItem("pm-oneoff-completed-open") === "1";
        }
        catch {
            return false;
        }
    });
    async function load() {
        try {
            const feed = await api.get("/users/me/one-off-tasks");
            setRows(feed.tasks);
        }
        catch {
            /* leave whatever we had */
        }
        finally {
            setLoaded(true);
        }
    }
    useEffect(() => {
        void load();
        const onFocus = () => void load();
        window.addEventListener("focus", onFocus);
        return () => window.removeEventListener("focus", onFocus);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshSignal]);
    // "on calendar Thu" / "needs a slot" — the status line each open row
    // shows to the right of its title.
    function statusOf(t) {
        if (t.scheduled_at) {
            const day = new Date(t.scheduled_at).toLocaleDateString(undefined, {
                weekday: "short",
            });
            return { label: `on calendar ${day}`, cls: "" };
        }
        return { label: "Needs Scheduling", cls: "warn" };
    }
    function fmtDue(iso) {
        if (!iso)
            return null;
        const d = new Date(`${iso}T00:00:00`);
        if (Number.isNaN(d.getTime()))
            return null;
        return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    }
    // "today" / "yesterday" / "N days ago" for the completed list.
    function relCompleted(iso) {
        if (!iso)
            return "";
        const then = new Date(iso);
        const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
        const days = Math.round((startOfDay(new Date()) - startOfDay(then)) / 86_400_000);
        if (days <= 0)
            return "today";
        if (days === 1)
            return "yesterday";
        return `${days} days ago`;
    }
    // Completed THIS calendar month roll into the collapsible section; older
    // completed ones are hidden (the list "purges" at month end).
    const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime();
    const activeRows = rows.filter((t) => !t.is_completed);
    const completedThisMonth = rows
        .filter((t) => t.is_completed &&
        t.completed_at != null &&
        new Date(t.completed_at).getTime() >= monthStart)
        .sort((a, b) => new Date(b.completed_at ?? 0).getTime() -
        new Date(a.completed_at ?? 0).getTime());
    function toggleCompleted() {
        const next = !completedOpen;
        setCompletedOpen(next);
        try {
            localStorage.setItem("pm-oneoff-completed-open", next ? "1" : "0");
        }
        catch {
            /* ignore */
        }
    }
    // Don't render until we know there's something — keeps the sidebar tidy
    // for users who've never made a one-off task.
    if (loaded && activeRows.length === 0 && completedThisMonth.length === 0)
        return null;
    return (_jsxs("section", { className: "pm-section", children: [_jsxs("div", { className: "pm-section-label", children: ["\uD83D\uDCCB ONE-OFF TASKS \u00B7 ", activeRows.length] }), completedThisMonth.length > 0 && (_jsxs("div", { className: "pm-oneoff-completed", children: [_jsxs("button", { type: "button", className: "pm-oneoff-completed-head", onClick: toggleCompleted, "aria-expanded": completedOpen, children: [_jsxs("span", { children: ["\u2713 Completed \u00B7 ", completedThisMonth.length] }), _jsx("span", { "aria-hidden": "true", children: completedOpen ? "▾" : "▸" })] }), completedOpen && (_jsx("ul", { className: "pm-oneoff-list", children: completedThisMonth.map((t) => (_jsxs("li", { className: `pm-oneoff-row done ${activeId === t.project_id ? "active" : ""}`, onClick: () => onSelect(t.project_id), role: "button", tabIndex: 0, onKeyDown: (e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    onSelect(t.project_id);
                                }
                            }, children: [_jsx("span", { className: "pm-oneoff-title", children: t.title }), t.category_label && (_jsx("span", { className: "pill pm-oneoff-cat", children: t.category_label })), _jsx("span", { className: "pm-oneoff-status muted", children: relCompleted(t.completed_at) })] }, t.project_id))) }))] })), activeRows.length > 0 && (_jsxs("div", { className: "pm-oneoff-subhead", children: ["Outstanding \u00B7 ", activeRows.length] })), _jsx("ul", { className: "pm-oneoff-list", children: activeRows.map((t) => {
                    const st = statusOf(t);
                    return (_jsxs("li", { className: `pm-oneoff-row ${activeId === t.project_id ? "active" : ""}`, onClick: () => onSelect(t.project_id), role: "button", tabIndex: 0, onKeyDown: (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                onSelect(t.project_id);
                            }
                        }, children: [_jsx("span", { className: "pm-oneoff-title", children: t.title }), t.category_label && (_jsx("span", { className: "pill pm-oneoff-cat", children: t.category_label })), fmtDue(t.due_date) && (_jsxs("span", { className: "pm-oneoff-due muted small", children: ["due ", fmtDue(t.due_date)] })), _jsx("span", { className: `pm-oneoff-status ${st.cls}`, children: st.label })] }, t.project_id));
                }) })] }));
}
// One-time events widget — the unified meeting list. Mirrors
// OneOffTasksWidget (self-fetching active rows + a collapsible
// Completed-this-month section) so the sidebar and table show the SAME
// list. Each active row surfaces the meeting time + attendees and, for
// managers, an "Edit" affordance that opens the reschedule / reassign
// editor — so a one-time meeting can be retimed or its attendees changed
// straight from the Projects page without detouring to the calendar.
export function OneTimeEventsWidget({ activeId, onSelect, canManage, refreshSignal = 0, onChanged, }) {
    const [rows, setRows] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [editing, setEditing] = useState(null);
    const [completedOpen, setCompletedOpen] = useState(() => {
        try {
            return localStorage.getItem("pm-onetime-completed-open") === "1";
        }
        catch {
            return false;
        }
    });
    async function load() {
        try {
            const feed = await api.get("/users/me/one-time-events");
            setRows(feed.events);
        }
        catch {
            /* leave whatever we had */
        }
        finally {
            setLoaded(true);
        }
    }
    useEffect(() => {
        void load();
        const onFocus = () => void load();
        window.addEventListener("focus", onFocus);
        return () => window.removeEventListener("focus", onFocus);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshSignal]);
    // "Tue 3:00 PM" — the meeting moment. "Needs a time" when no block exists.
    function fmtEventAt(iso) {
        if (!iso)
            return { label: "Needs a time", cls: "warn" };
        const d = new Date(iso);
        if (Number.isNaN(d.getTime()))
            return { label: "Needs a time", cls: "warn" };
        return {
            label: d.toLocaleString(undefined, {
                weekday: "short",
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
            }),
            cls: "",
        };
    }
    function relCompleted(iso) {
        if (!iso)
            return "";
        const then = new Date(iso);
        const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
        const days = Math.round((startOfDay(new Date()) - startOfDay(then)) / 86_400_000);
        if (days <= 0)
            return "today";
        if (days === 1)
            return "yesterday";
        return `${days} days ago`;
    }
    const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime();
    // Active = not completed, sorted soonest meeting first (events with no
    // time sink to the bottom).
    const activeRows = rows
        .filter((e) => !e.is_completed)
        .sort((a, b) => {
        const ta = a.event_at ? new Date(a.event_at).getTime() : Infinity;
        const tb = b.event_at ? new Date(b.event_at).getTime() : Infinity;
        return ta - tb;
    });
    const completedThisMonth = rows
        .filter((e) => e.is_completed &&
        e.completed_at != null &&
        new Date(e.completed_at).getTime() >= monthStart)
        .sort((a, b) => new Date(b.completed_at ?? 0).getTime() -
        new Date(a.completed_at ?? 0).getTime());
    function toggleCompleted() {
        const next = !completedOpen;
        setCompletedOpen(next);
        try {
            localStorage.setItem("pm-onetime-completed-open", next ? "1" : "0");
        }
        catch {
            /* ignore */
        }
    }
    if (loaded && activeRows.length === 0 && completedThisMonth.length === 0)
        return null;
    return (_jsxs("section", { className: "pm-section", children: [_jsxs("div", { className: "pm-section-label", children: ["\uD83D\uDCCD ONE-TIME EVENTS \u00B7 ", activeRows.length] }), completedThisMonth.length > 0 && (_jsxs("div", { className: "pm-oneoff-completed", children: [_jsxs("button", { type: "button", className: "pm-oneoff-completed-head", onClick: toggleCompleted, "aria-expanded": completedOpen, children: [_jsxs("span", { children: ["\u2713 Completed \u00B7 ", completedThisMonth.length] }), _jsx("span", { "aria-hidden": "true", children: completedOpen ? "▾" : "▸" })] }), completedOpen && (_jsx("ul", { className: "pm-oneoff-list", children: completedThisMonth.map((e) => (_jsxs("li", { className: `pm-oneoff-row done ${activeId === e.project_id ? "active" : ""}`, onClick: () => onSelect(e.project_id), role: "button", tabIndex: 0, onKeyDown: (ev) => {
                                if (ev.key === "Enter" || ev.key === " ") {
                                    ev.preventDefault();
                                    onSelect(e.project_id);
                                }
                            }, children: [_jsx("span", { className: "pm-oneoff-title", children: e.title }), e.category_label && (_jsx("span", { className: "pill pm-oneoff-cat", children: e.category_label })), _jsx("span", { className: "pm-oneoff-status muted", children: relCompleted(e.completed_at) })] }, e.project_id))) }))] })), activeRows.length > 0 && (_jsxs("div", { className: "pm-oneoff-subhead", children: ["Outstanding \u00B7 ", activeRows.length] })), _jsx("ul", { className: "pm-oneoff-list", children: activeRows.map((e) => {
                    const st = fmtEventAt(e.event_at);
                    const attendeeNames = e.attendees.map((a) => a.name).join(", ");
                    return (_jsxs("li", { className: `pm-oneoff-row pm-onetime-row ${activeId === e.project_id ? "active" : ""}`, onClick: () => onSelect(e.project_id), role: "button", tabIndex: 0, onKeyDown: (ev) => {
                            if (ev.key === "Enter" || ev.key === " ") {
                                ev.preventDefault();
                                onSelect(e.project_id);
                            }
                        }, children: [_jsx("span", { className: "pm-oneoff-title", children: e.title }), e.category_label && (_jsx("span", { className: "pill pm-oneoff-cat", children: e.category_label })), _jsx("span", { className: `pm-oneoff-status ${st.cls}`, children: st.label }), attendeeNames && (_jsx("span", { className: "pm-onetime-attendees muted small", children: e.attendees.length === 1
                                    ? attendeeNames
                                    : `${e.attendees.length} attendees · ${attendeeNames}` })), canManage && (_jsx("button", { type: "button", className: "btn-link pm-onetime-edit", onClick: (ev) => {
                                    ev.stopPropagation();
                                    setEditing(e);
                                }, title: "Reschedule or change attendees", children: "Edit" }))] }, e.project_id));
                }) }), editing && (_jsx(MeetingEditor, { event: editing, onClose: () => setEditing(null), onSaved: async () => {
                    setEditing(null);
                    await load();
                    onChanged?.();
                } }))] }));
}
// Reschedule + reassign editor for a one-time event. Prefilled from the
// OneTimeEventRow (meeting time, duration, attendees). Saving PATCHes
// /projects/{id}/event, which moves every attendee's calendar block to the
// new time and adds/removes blocks for added/removed attendees — the whole
// team's calendars stay in sync in one call.
function MeetingEditor({ event, onClose, onSaved, }) {
    function splitLocal(iso) {
        const pad = (n) => String(n).padStart(2, "0");
        if (iso) {
            const d = new Date(iso);
            if (!Number.isNaN(d.getTime())) {
                return {
                    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
                    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
                };
            }
        }
        return { date: "", time: "09:00" };
    }
    const initial = splitLocal(event.event_at);
    const [date, setDate] = useState(initial.date);
    const [time, setTime] = useState(initial.time);
    const [duration, setDuration] = useState(event.duration_minutes || 30);
    const [attendeeIds, setAttendeeIds] = useState(event.attendees.map((a) => a.id));
    const [roster, setRoster] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                // BU-scoped roster (includes the caller). Admins get everyone.
                const list = await api.get("/users/coworkers?include_self=true");
                if (!cancelled)
                    setRoster(list);
            }
            catch {
                /* leave empty — at minimum we still show existing attendees */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);
    // Anyone already invited but not in the roster (e.g. a cross-BU admin)
    // still needs to render as a removable chip, so merge them in.
    const rosterById = new Map(roster.map((u) => [u.id, u.name]));
    for (const a of event.attendees) {
        if (!rosterById.has(a.id))
            rosterById.set(a.id, a.name);
    }
    const pickList = Array.from(rosterById.entries())
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name));
    function toggle(uid) {
        setAttendeeIds((cur) => cur.includes(uid) ? cur.filter((x) => x !== uid) : [...cur, uid]);
    }
    async function save() {
        setSaving(true);
        setError(null);
        try {
            let eventAtIso = null;
            if (date) {
                const d = new Date(`${date}T${time}:00`);
                if (!Number.isNaN(d.getTime()))
                    eventAtIso = d.toISOString();
            }
            await api.patch(`/projects/${event.project_id}/event`, {
                event_at: eventAtIso,
                duration_minutes: duration,
                attendee_ids: attendeeIds,
            });
            await onSaved();
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't save the meeting.");
            setSaving(false);
        }
    }
    return (_jsx("div", { className: "modal-backdrop", onClick: onClose, children: _jsxs("div", { className: "modal", onClick: (e) => e.stopPropagation(), children: [_jsx("h2", { children: "Edit meeting" }), _jsxs("p", { className: "muted small", style: { marginTop: "-0.5rem" }, children: ["Reschedule moves \u201C", event.title, "\u201D on every attendee's calendar. Adding or removing people updates their calendars too. If a copy ever drifts out of sync, hit ", _jsx("strong", { children: "Resync calendars" }), " to pull everyone back to this time."] }), _jsxs("div", { className: "row", children: [_jsxs("label", { className: "project-type-label", children: ["Date", _jsx("input", { type: "date", value: date, onChange: (e) => setDate(e.target.value) })] }), _jsxs("label", { className: "project-type-label", children: ["Time", _jsx(TimeSelect, { value: time, onChange: setTime })] }), _jsxs("label", { className: "project-type-label", children: ["Length", _jsxs("select", { value: duration, onChange: (e) => setDuration(Number(e.target.value)), children: [_jsx("option", { value: 15, children: "15 min" }), _jsx("option", { value: 30, children: "30 min" }), _jsx("option", { value: 45, children: "45 min" }), _jsx("option", { value: 60, children: "1 hour" }), _jsx("option", { value: 90, children: "1.5 hours" }), _jsx("option", { value: 120, children: "2 hours" })] })] })] }), _jsx("label", { className: "project-type-label", children: "Attendees" }), _jsxs("div", { className: "row", style: { flexWrap: "wrap", gap: "0.35rem", marginTop: "0.3rem" }, children: [pickList.map((u) => {
                            const picked = attendeeIds.includes(u.id);
                            return (_jsx("button", { type: "button", className: `dow-btn ${picked ? "active" : ""}`, onClick: () => toggle(u.id), children: u.name }, u.id));
                        }), pickList.length === 0 && (_jsx("span", { className: "muted small", children: "No teammates available." }))] }), !date && (_jsx("p", { className: "muted small", children: "No date set \u2014 pick one to put this meeting on calendars." })), error && _jsx("div", { className: "error", children: error }), _jsxs("div", { className: "modal-actions", children: [_jsx("button", { type: "button", onClick: onClose, disabled: saving, children: "Cancel" }), _jsx("button", { type: "button", onClick: save, disabled: saving || !date, title: "Pull every attendee's copy of this meeting to the time shown above", style: { marginRight: "auto" }, children: saving ? "Working…" : "↻ Resync calendars" }), _jsx("button", { type: "button", className: "btn-primary", onClick: save, disabled: saving, children: saving ? "Saving…" : "Save meeting" })] })] }) }));
}
// One-time event meeting panel for the project detail pane. Shows the
// meeting time + attendee list and, for people who can manage it (or the
// creator), a button that opens the same MeetingEditor used on the list —
// so a meeting can be rescheduled / reassigned straight from the project
// page. Prefilled from the project tree (event_at + assignees).
export function OneTimeMeetingPanel({ tree, canEdit, onChanged, }) {
    const [editing, setEditing] = useState(false);
    const task = tree.tasks[0];
    const attendees = (tree.assignee_ids ?? []).map((id, i) => ({
        id,
        name: tree.assignee_names?.[i] ?? "Teammate",
    }));
    const whenLabel = tree.event_at
        ? new Date(tree.event_at).toLocaleString(undefined, {
            weekday: "long",
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
        })
        : "No time set yet";
    const eventRow = {
        project_id: tree.id,
        task_id: task?.id ?? "",
        title: tree.name,
        category_label: null,
        category_kind: null,
        event_at: tree.event_at ?? null,
        duration_minutes: tree.event_duration_minutes ?? 30,
        is_completed: false,
        attendees,
    };
    return (_jsxs("section", { className: "project-meeting-panel", children: [_jsxs("div", { className: "project-meeting-row", children: [_jsxs("div", { className: "project-meeting-info", children: [_jsx("div", { className: "muted small", children: "\uD83D\uDCCD Meeting" }), _jsxs("div", { className: "project-meeting-when", children: [whenLabel, tree.event_duration_minutes
                                        ? ` · ${tree.event_duration_minutes} min`
                                        : ""] }), _jsx("div", { className: "muted small", children: attendees.length === 0
                                    ? "No attendees yet"
                                    : `${attendees.length} attendee${attendees.length === 1 ? "" : "s"}: ${attendees.map((a) => a.name).join(", ")}` })] }), canEdit && task && (_jsx("button", { type: "button", className: "btn-primary", onClick: () => setEditing(true), children: "Reschedule / attendees" }))] }), editing && (_jsx(MeetingEditor, { event: eventRow, onClose: () => setEditing(false), onSaved: async () => {
                    setEditing(false);
                    onChanged();
                } }))] }));
}
// Sidebar project list with HTML5 drag-and-drop reordering. Each bin
// gets its own storageKey; the order is per-user (localStorage) and
// applied on every render before passing down to SidebarProjectRow.
// Drag the row by its grip → drop on another row → the dragged item
// slots in at that position. No backend round-trip — this is a personal
// preference, not shared state.
function DraggableProjectList({ projects, activeId, onSelect, storageKey, pinnedIds, onTogglePin, expanded, units, }) {
    // Lift the order into state so reorders re-render without a fetch.
    // Seed from localStorage on first mount; subsequent project-list
    // changes (creates/deletes) are reconciled via applyStoredOrder.
    const [order, setOrder] = useState(() => readStoredOrder(storageKey));
    const ordered = useMemo(() => applyStoredOrder(projects, order), [projects, order]);
    // Track the dragged row's id across handlers without forcing
    // re-renders mid-drag (state would flush every dragover).
    const dragId = useRef(null);
    function onDragStart(e, id) {
        dragId.current = id;
        e.dataTransfer.effectAllowed = "move";
        // Some browsers (Firefox) refuse to start the drag unless
        // setData has been called.
        try {
            e.dataTransfer.setData("text/plain", id);
        }
        catch {
            /* ignore — Chrome doesn't need this */
        }
    }
    function onDragOver(e) {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    }
    function onDrop(e, targetId) {
        e.preventDefault();
        const src = dragId.current;
        dragId.current = null;
        if (!src || src === targetId)
            return;
        // Build the new order by removing src and inserting it at
        // target's position (drop-before semantics).
        const ids = ordered.map((p) => p.id);
        const fromIdx = ids.indexOf(src);
        const toIdx = ids.indexOf(targetId);
        if (fromIdx < 0 || toIdx < 0)
            return;
        ids.splice(fromIdx, 1);
        ids.splice(toIdx, 0, src);
        setOrder(ids);
        writeStoredOrder(storageKey, ids);
    }
    return (_jsx("ul", { className: `project-list ${expanded ? "project-list-expanded" : ""}`, children: ordered.map((p) => (_jsx("li", { draggable: true, onDragStart: (e) => onDragStart(e, p.id), onDragOver: onDragOver, onDrop: (e) => onDrop(e, p.id), className: "project-row-wrap", title: "Drag to reorder \u00B7 click to open", children: _jsx(SidebarProjectRow, { project: p, active: p.id === activeId, onSelect: () => onSelect(p.id), isPinned: pinnedIds.has(p.id), onTogglePin: () => onTogglePin(p.id), expanded: expanded, units: units }) }, p.id))) }));
}
// CollapsibleBin was retired when the sidebar moved to a tabs + search +
// pinned model — the per-bin collapse turned out to be friction more
// than help, so each section is now an always-open uppercase label
// strip. Kept as a comment so the diff search points here.
function SidebarProjectRow({ project, active, onSelect, isPinned, onTogglePin, expanded, units, }) {
    const names = project.assignee_names ?? [];
    // Show every assignee's name comma-separated — the previous "+N more"
    // truncation hid teammates that workers needed to see at a glance.
    // Long names wrap inside the row.
    const namesShort = names.length === 0 ? "Unassigned" : names.join(", ");
    const buName = units.find((u) => u.id === project.business_unit_id)?.name;
    const isMeeting = project.type === "recurring_event" || project.type === "one_time_event";
    return (_jsxs("div", { className: `project-row ${active ? "active" : ""}`, children: [_jsx("button", { className: "project-pin", onClick: (e) => {
                    e.stopPropagation();
                    onTogglePin();
                }, title: isPinned ? "Unpin from top" : "Pin to top", "aria-pressed": isPinned, children: _jsx("span", { className: isPinned ? "pinned" : "", "aria-hidden": "true", children: "\u2605" }) }), _jsxs("button", { className: `project-link ${expanded ? "project-link-expanded" : ""}`, onClick: onSelect, title: project.due_date
                    ? `Due ${project.due_date} · priority ${project.priority} (1 = urgent)`
                    : `Priority ${project.priority} (1 = urgent)`, children: [_jsx("span", { className: `priority-pip pip-${priorityTier(project.priority)}`, "aria-hidden": "true" }), _jsxs("div", { className: "project-link-body", children: [_jsxs("div", { className: "project-link-name-row", children: [_jsxs("span", { className: "project-link-name", children: [isMeeting && (_jsx("span", { className: "project-link-icon", "aria-hidden": "true", children: project.type === "recurring_event" ? "↻ " : "📍 " })), project.name] }), project.due_date && (_jsx("span", { className: "project-link-due", children: fmtSidebarDate(project.due_date) }))] }), expanded ? (_jsxs("div", { className: "project-link-sub project-link-sub-expanded", children: [_jsx("span", { className: `project-link-priority pri-${priorityTier(project.priority)}`, title: project.priority == null
                                            ? "Priority not set (N/A)"
                                            : "Project priority (1 = urgent)", children: project.priority == null ? "P—" : `P${project.priority}` }), buName && (_jsx("span", { className: "project-link-bu", children: buName })), _jsx("span", { className: "project-link-assignees", children: namesShort })] })) : (_jsxs("div", { className: "project-link-sub", children: [_jsxs("span", { className: "project-link-priority", title: "Project priority (1 = urgent)", children: ["P", project.priority] }), _jsx("span", { className: "project-link-assignees", children: namesShort })] }))] })] })] }));
}
